/**
 * What may be dialed from the eMerge line, and how a number is shown.
 *
 * The line is a US number, so the Dialer places North American calls and
 * nothing else. Three kinds are refused here, before Amazon Connect is ever
 * asked, because a person should hear why in plain words rather than meet a
 * silent failure:
 *
 *   emergency numbers   Amazon Connect is not an emergency-calling service.
 *                       A caller who needs 911 must be told to use a phone
 *                       that is, immediately and unmistakably.
 *   premium-rate (900)  a charge per minute to the organisation.
 *   anything non-+1     international calling is not part of this line.
 *
 * Connect applies its own restrictions as well; this is the first gate, not
 * the only one.
 */

export type NumberProblem = 'EMPTY' | 'EMERGENCY' | 'NOT_US' | 'PREMIUM' | 'INCOMPLETE' | 'TOO_LONG' | 'INVALID';

export type NumberCheck =
  | { ok: true; e164: string; display: string }
  | { ok: false; problem: NumberProblem };

const EMERGENCY = new Set(['911', '112', '988', '933']);

export const PROBLEM_TEXT: Record<NumberProblem, string> = {
  EMPTY: 'Enter a number to call.',
  EMERGENCY: 'Emergency calls can’t be made from the Dialer. Use a regular phone now.',
  NOT_US: 'Only US numbers can be called from this line.',
  PREMIUM: 'Premium-rate 900 numbers can’t be called from this line.',
  INCOMPLETE: 'That number is too short. US numbers have 10 digits.',
  TOO_LONG: 'That number is too long. US numbers have 10 digits.',
  INVALID: 'That isn’t a valid US number. Check the area code.',
};

/** (813) 582-4873 for ten NANP digits. */
export function formatTen(ten: string): string {
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/** A number from Amazon Connect, shown the way a person reads it. Anything unrecognised is shown as given. */
export function formatForDisplay(value: string | null | undefined): string {
  if (!value) return 'Unknown number';
  const m = /^\+1(\d{10})$/.exec(value.trim());
  return m ? formatTen(m[1]) : value;
}

export function checkNumber(input: string): NumberCheck {
  const raw = input.trim();
  if (!raw) return { ok: false, problem: 'EMPTY' };

  let digits = raw.replace(/\D/g, '');
  if (!digits) return { ok: false, problem: 'INVALID' };

  // Emergency short codes, with or without a leading 1, are refused first
  // and loudest, whatever else the input looks like.
  if (EMERGENCY.has(digits) || (digits.length === 4 && digits.startsWith('1') && EMERGENCY.has(digits.slice(1)))) {
    return { ok: false, problem: 'EMERGENCY' };
  }

  const plus = raw.startsWith('+');
  if (plus) {
    if (!digits.startsWith('1')) return { ok: false, problem: 'NOT_US' };
    digits = digits.slice(1);
  } else if (digits.startsWith('011') || digits.startsWith('00')) {
    return { ok: false, problem: 'NOT_US' };
  } else if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  if (digits.length < 10) return { ok: false, problem: 'INCOMPLETE' };
  if (digits.length > 10) return { ok: false, problem: 'TOO_LONG' };

  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  if (area === '900') return { ok: false, problem: 'PREMIUM' };
  // NANP: neither the area code nor the exchange starts with 0 or 1, and
  // neither is an N11 service code.
  if (!/^[2-9]\d{2}$/.test(area) || !/^[2-9]\d{2}$/.test(exchange)) return { ok: false, problem: 'INVALID' };
  if (area.slice(1) === '11' || exchange.slice(1) === '11') return { ok: false, problem: 'INVALID' };

  return { ok: true, e164: `+1${digits}`, display: formatTen(digits) };
}

/** Characters a person may type into the number field. Everything else is dropped as typed. */
export function cleanTyped(input: string): string {
  return input.replace(/[^0-9+()\-.\s*#]/g, '').slice(0, 24);
}
